import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, Box, CircularProgress, Typography } from '@mui/material';
import type { Budget } from '@pflegenavi/frontend/api-nursing-home';
import type { BudgetStatus } from '@pflegenavi/shared/frontend';
import { budgetStatus, budgetStatusTone } from '@pflegenavi/shared/frontend';
import { useFormatting } from '@pflegenavi/frontend/localization';

/** `ok` is the only status that varies by context; pass `okColor` to override it. */
export const budgetIndicatorColor = (
  status: BudgetStatus,
  okColor = 'success.main'
): string => (status === 'ok' ? okColor : `${budgetStatusTone(status)}.main`);

interface BudgetUsageCircleProps {
  /** Usage in percent; may exceed 100. The ring caps at 100; the label shows the rounded value, or ">100%" when non-finite. */
  percent: number;
  status: BudgetStatus;
  size?: number;
}

export const BudgetUsageCircle: FC<BudgetUsageCircleProps> = ({
  percent,
  status,
  size = 48,
}) => {
  const label = Number.isFinite(percent) ? `${Math.round(percent)}` : '>100';
  const ringColor = budgetIndicatorColor(status);
  const tone = budgetStatusTone(status);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{ color: 'grey.50016' }}
      />
      <CircularProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        size={size}
        thickness={4}
        sx={{
          color: ringColor,
          position: 'absolute',
          left: 0,
        }}
      />
      <Box
        sx={{
          inset: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette[tone].main, 0.12),
        }}
      >
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontWeight: 700 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

interface BudgetStatusHintProps {
  budget: Budget;
}

/**
 * One-line explanation of a budget's state, colored like the usage ring except
 * that the "everything okay" case stays muted rather than green.
 */
export const BudgetStatusHint: FC<BudgetStatusHintProps> = ({ budget }) => {
  const { t } = useTranslation();
  const status = budgetStatus(budget);
  const activeAlerts = (budget.alerts ?? []).filter(
    (alert) => alert.active
  ).length;

  return (
    <Typography
      variant="body2"
      color={budgetIndicatorColor(status, 'text.secondary')}
    >
      {status === 'exceeded' || status === 'ok'
        ? t(`budgets.hint.${status}`)
        : t('budgets.hint.alerts', { count: activeAlerts })}
    </Typography>
  );
};

interface BudgetAvailableAmountProps {
  availableCents: number;
}

export const BudgetAvailableAmount: FC<BudgetAvailableAmountProps> = ({
  availableCents,
}) => {
  const { fCurrencyCents } = useFormatting();
  return (
    <Typography
      variant="subtitle2"
      color={availableCents >= 0 ? 'success.main' : 'error.main'}
    >
      {fCurrencyCents(availableCents)}
    </Typography>
  );
};
